import { useParams } from 'react-router-dom';
import { config } from "../../data/config";
import { useEffect } from 'react';
import { getCookies } from "cookies-next";
import axios from "axios";
import { useState } from "react";
import BreadCrump from "../tools/breadcrump"
import { useTranslation } from 'react-i18next';
import HeaderOfPage from '../tools/headerOfPage';
import Swal from 'sweetalert2';
import ImageUploader from '../tools/uploaderImage';

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';

export default function EditTestimonialGroup() {
    const { t } = useTranslation();
    let cookie = getCookies();
    let { Id } = useParams();
    const [key, setKey] = useState<any>(1);
    const [error, setError] = useState<any>(null);
    const [activeLang, setActiveLang] = useState<LanguageCode>('en');
    const languages = [
        { code: 'ku' as LanguageCode, label: 'Kurdish' },
        { code: 'fa' as LanguageCode, label: 'فارسی' },
        { code: 'en' as LanguageCode, label: 'English' },
        { code: 'ar' as LanguageCode, label: 'العربية' },
        { code: 'tr' as LanguageCode, label: 'Türkçe' }
    ];

    const [data, setData] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [translating, setTranslating] = useState<boolean>(false);

    // Translate text: try backend first, then Google fallback
    const translateText = async (text: string, fromLang: LanguageCode, toLang: LanguageCode): Promise<string> => {
        if (!text || String(text).trim() === '') return '';
        if (fromLang === toLang) return text;
        const from = fromLang;
        const to = toLang;
        try {
            try {
                const response = await axios.post(
                    `${config.url}/v1/translate`,
                    { text: text.trim(), from, to, format: 'text' },
                    { headers: { Authorization: 'Bearer ' + cookie['token'] } }
                );
                if (response.data?.translatedText) return response.data.translatedText;
            } catch (_) {
                // Backend not available, use Google
            }
            const response = await fetch(
                `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${from}&tl=${to}&dt=t&q=${encodeURIComponent(text.trim())}`
            );
            if (!response.ok) return '';
            const result = await response.json();
            const translated = result[0]?.map((s: any) => s?.[0] || '').join('').trim();
            return translated || text;
        } catch (_) {
            return '';
        }
    };

    const handleAutoTranslate = async () => {
        const titleText = (data.title?.[activeLang] || '').trim();
        const descText = (data.description?.[activeLang] || '').trim();
        if (!titleText && !descText) {
            Swal.fire({
                title: t('No content to translate'),
                text: t('Please add content in the current language first'),
                icon: 'warning',
            });
            return;
        }
        const confirmed = await Swal.fire({
            title: t('Auto translate'),
            text: t('This will translate the current content to all other languages. Continue?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('Yes, translate'),
            cancelButtonText: t('Cancel'),
        });
        if (!confirmed.isConfirmed) return;

        setTranslating(true);
        try {
            const targetLangs = languages.map((l) => l.code).filter((code) => code !== activeLang);
            const newTitle = { ...data.title };
            const newDescription = { ...data.description };

            for (const toLang of targetLangs) {
                if (titleText) {
                    const translated = await translateText(titleText, activeLang, toLang);
                    if (translated) newTitle[toLang] = translated;
                    await new Promise((r) => setTimeout(r, 120));
                }
                if (descText) {
                    const translated = await translateText(descText, activeLang, toLang);
                    if (translated) newDescription[toLang] = translated;
                    await new Promise((r) => setTimeout(r, 120));
                }
            }
            setData({ ...data, title: newTitle, description: newDescription });
            Swal.fire({
                title: t('Translation completed'),
                text: t('Content has been translated to all other languages'),
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (err: any) {
            Swal.fire({
                title: t('Translation failed'),
                text: err?.message || t('An error occurred during translation'),
                icon: 'error',
            });
        } finally {
            setTranslating(false);
        }
    };
    
    const save = async () => {
        setSaving(true);
        try {
            var path = `${config.url}/v1/dashboard/testimonialgroup/${Id}`;

            let res = await axios({
                method: 'put',
                data: data,
                url: path,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });
            Swal.fire({
                icon: 'success',
                title: t('Success'),
                text: t('Group updated successfully'),
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Error updating group'),
            });
        } finally {
            setSaving(false);
        }
    }
    
    const load = async () => {
        try {
            var path = `${config.url}/v1/dashboard/testimonialgroup/${Id}`;

            let res = await axios({
                method: 'get',
                url: path,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });
            const loadedData = res.data.message;
            // Ensure multilingual structure exists
            if (loadedData) {
                if (typeof loadedData.title === 'string' || !loadedData.title) {
                    loadedData.title = loadedData.title ? { ku: loadedData.title, fa: loadedData.title, en: loadedData.title, ar: loadedData.title, tr: loadedData.title } : { ku: '', fa: '', en: '', ar: '', tr: '' };
                } else if (loadedData.title && loadedData.title.nl) {
                    loadedData.title.ku = loadedData.title.nl;
                    delete loadedData.title.nl;
                    if (!loadedData.title.tr) loadedData.title.tr = '';
                }
                if (typeof loadedData.description === 'string' || !loadedData.description) {
                    loadedData.description = loadedData.description ? { ku: loadedData.description, fa: loadedData.description, en: loadedData.description, ar: loadedData.description, tr: loadedData.description } : { ku: '', fa: '', en: '', ar: '', tr: '' };
                } else if (loadedData.description && loadedData.description.nl) {
                    loadedData.description.ku = loadedData.description.nl;
                    delete loadedData.description.nl;
                    if (!loadedData.description.tr) loadedData.description.tr = '';
                }
            }
            setData(loadedData)
        } catch (error) {
            setError(error);
        }
    }
    
    const inputstyle = "py-3 px-5 block w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:pointer-events-none bg-white text-gray-900";
    
    const breadcrump = [
        {
            name: 'Dashboard',
            url: '/dashboard'
        },
        {
            name: 'Testimonial',
            url: '/dashboard/Testimonial'
        },
        {
            name: 'Group List',
            url: '/dashboard/Testimonial/GroupList'
        },
        {
            name: 'Edit Group',
            url: null
        }
    ]
    
    useEffect(() => {
        load();
    }, []);
    
    if (!data) {
        return (
            <div className="bg-gray-50 min-h-screen p-4 md:p-6">
                <BreadCrump data={breadcrump}></BreadCrump>
                <HeaderOfPage name="Edit Testimonial Group"></HeaderOfPage>
                <div className="bg-white rounded-xl shadow-sm p-8 mt-6">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                    <p className="text-center mt-4 text-gray-600">در حال بارگذاری...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-6">
            <BreadCrump data={breadcrump}></BreadCrump>
            <HeaderOfPage name="Edit Testimonial Group"></HeaderOfPage>

            {data != null && (
                <div className="mt-6">
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden" dir='rtl'>
                        <div className="p-6 space-y-6">
                            <form key={key} className='flex flex-col gap-4'>
                                {/* Language Selector + Auto Translate */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('Language')}
                                    </label>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {languages.map((lang) => (
                                            <button
                                                key={lang.code}
                                                type="button"
                                                onClick={() => setActiveLang(lang.code)}
                                                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                    activeLang === lang.code
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                            >
                                                {lang.label}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={handleAutoTranslate}
                                            disabled={translating}
                                            title={t('Auto translate to all other languages')}
                                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-green-600 text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {translating ? (
                                                <>
                                                    <span className="animate-spin inline-block w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full" />
                                                    <span>{t('Translating...')}</span>
                                                </>
                                            ) : (
                                                <span>{t('Auto translate to all other languages')}</span>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('Title')} ({languages.find(l => l.code === activeLang)?.label}) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        value={data.title?.[activeLang] || ''}
                                        type="text"
                                        className={`${inputstyle} ${error != null && error['title'] != undefined && 'bg-red-100 border-red-300'} `}
                                        placeholder={t('Enter title')}
                                        onChange={(e) => {
                                            setData({ 
                                                ...data, 
                                                title: { 
                                                    ...data.title, 
                                                    [activeLang]: e.target.value 
                                                }
                                            });
                                        }}
                                    />
                                    {error != null && error['title'] != undefined && <small className="text-red-500 text-xs mt-1 block">{error['title']['message']}</small>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('Description')} ({languages.find(l => l.code === activeLang)?.label})
                                    </label>
                                    <textarea
                                        value={data.description?.[activeLang] || ''}
                                        rows={4}
                                        className={inputstyle}
                                        placeholder={t('Enter description')}
                                        onChange={(e) => {
                                            setData({ 
                                                ...data, 
                                                description: { 
                                                    ...data.description, 
                                                    [activeLang]: e.target.value 
                                                }
                                            });
                                        }}
                                    />
                                </div>

                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={data.publish !== false}
                                            onChange={(e) => setData({ ...data, publish: e.target.checked })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">{t('Publish')}</span>
                                    </label>
                                </div>
                                <div>
                                    <ImageUploader
                                    id={data.id}
                                    component="testimonialgroup"

                                    />
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={save}
                                        disabled={saving}
                                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {saving ? t('Saving...') : t('Save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

